import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PromiseStatuses } from "../../lib/types";
import {
  FindAllParticularitiesResponse,
  ParticularitiesFilters,
  ParticularityStatusEnum,
  UpsertParticularityDTO,
} from "./dto";
import { NewParticularitiesService } from "./service";

interface ParticularitiesState {
  findAllStatus: PromiseStatuses;
  findAllResponse?: FindAllParticularitiesResponse;
  filters: ParticularitiesFilters;
  showNewParticularityModal: boolean;
  editParticularityNameStatus: PromiseStatuses;
  createParticularityStatus: PromiseStatuses;
  createParticularityResponse?: string;
  deleteParticularityStatus: PromiseStatuses;
  selectedParticularity?: { name: string; id: string };
  updateParticularityStatus: PromiseStatuses;
  particularityToUpdate?: { id: string, status: ParticularityStatusEnum }
}

const initialState: ParticularitiesState = {
  findAllStatus: "idle",
  updateParticularityStatus: "idle",
  filters: {
    itemsPerPage: 50,
    order: true,
    name: '',
    page: 0,
    sort: "name",
  },
  showNewParticularityModal: false,
  createParticularityStatus: "idle",
  editParticularityNameStatus: "idle",
  deleteParticularityStatus: "idle",
};

export const findAllParticularities = createAsyncThunk(
  "particularities/findAllParticularities",
  async (
    filters: ParticularitiesFilters,
    thunkApi
  ): Promise<FindAllParticularitiesResponse> => {
    const ParticularitiesService = NewParticularitiesService();

    return ParticularitiesService.findAllParticularities(filters);
  }
);

export const createParticularity = createAsyncThunk(
  "particularities/createParticularity",
  async (request: UpsertParticularityDTO, thunkApi): Promise<string> => {
    const ParticularitiesService = NewParticularitiesService();

    return ParticularitiesService.createParticularity(request);
  }
);

export const deleteParticularity = createAsyncThunk(
  "particularities/deleteParticularity",
  async (request: string, thunkApi): Promise<void> => {
    const ParticularitiesService = NewParticularitiesService();

    return ParticularitiesService.deleteParticularity(request);
  }
);

export const editParticularityName = createAsyncThunk(
  "particularities/editParticularityName",
  async (
    request: { data: UpsertParticularityDTO; id: string },
    thunkApi
  ): Promise<void> => {
    const ParticularitiesService = NewParticularitiesService();

    return ParticularitiesService.editParticularity(request.data, request.id);
  }
);

export const updateParticularityStatus = createAsyncThunk(
  "particularities/updateParticularityStatus",
  async (
    request: { data: ParticularityStatusEnum; id: string },
    thunkApi
  ): Promise<void> => {
    const ParticularitiesService = NewParticularitiesService();

    return ParticularitiesService.editParticularityStatus(request.data, request.id);
  }
);

const particularitiesSlice = createSlice({
  name: "particularities/slice",
  initialState,
  extraReducers(builder) {
    builder
      .addCase(updateParticularityStatus.pending, (state) => {
        state.updateParticularityStatus = "loading";
      })
      .addCase(updateParticularityStatus.fulfilled, (state, action) => {
        state.updateParticularityStatus = "successfully";
      })
      .addCase(updateParticularityStatus.rejected, (state) => {
        state.updateParticularityStatus = "failed";
      })
      .addCase(editParticularityName.pending, (state) => {
        state.editParticularityNameStatus = "loading";
      })
      .addCase(editParticularityName.fulfilled, (state, action) => {
        state.editParticularityNameStatus = "successfully";
      })
      .addCase(editParticularityName.rejected, (state) => {
        state.editParticularityNameStatus = "failed";
      })
      .addCase(findAllParticularities.pending, (state) => {
        state.findAllStatus = "loading";
      })
      .addCase(findAllParticularities.fulfilled, (state, action) => {
        state.findAllStatus = "successfully";
        state.findAllResponse = action.payload;
      })
      .addCase(findAllParticularities.rejected, (state) => {
        state.findAllStatus = "failed";
      })
      .addCase(createParticularity.pending, (state) => {
        state.createParticularityStatus = "loading";
      })
      .addCase(createParticularity.fulfilled, (state, action) => {
        state.createParticularityStatus = "successfully";
        state.createParticularityResponse = action.payload;
      })
      .addCase(createParticularity.rejected, (state) => {
        state.createParticularityStatus = "failed";
      })
      .addCase(deleteParticularity.pending, (state) => {
        state.deleteParticularityStatus = "loading";
      })
      .addCase(deleteParticularity.fulfilled, (state, action) => {
        state.deleteParticularityStatus = "successfully";
      })
      .addCase(deleteParticularity.rejected, (state) => {
        state.deleteParticularityStatus = "failed";
      });
  },
  reducers: {
    setUpdateParticularityStatus: (state, action) => {
      state.updateParticularityStatus = action.payload;
    },
    setFindAllParticularitiesPage: (state, action) => {
      state.filters.page = action.payload;
    },
    setFindAllParticularitiesOrder: (state, action) => {
      state.filters.order = action.payload;
    },
    setFindAllParticularitiesItemsPerPage: (state, action) => {
      state.filters.itemsPerPage = action.payload;
    },
    setFindAllParticularitiesSort: (state, action) => {
      state.filters.sort = action.payload;
    },
    setFindAllParticularitiesStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    setShowNewParticularityModal: (state, action) => {
      state.showNewParticularityModal = action.payload;
    },
    setCreateParticularityStatus: (state, action) => {
      state.createParticularityStatus = action.payload;
    },
    setEditParticularityNameStatus: (state, action) => {
      state.editParticularityNameStatus = action.payload;
    },
    setDeleteParticularityStatus: (state, action) => {
      state.deleteParticularityStatus = action.payload;
    },
    setSelectedParticularity: (state, action) => {
      state.selectedParticularity = action.payload;
    },
    setCreateParticularityResponse: (state, action) => {
      state.createParticularityResponse = action.payload;
    },
    setParticularityToUpdate: (state, action) => {
      state.particularityToUpdate = action.payload;
    }
  },
});

export const {
  setFindAllParticularitiesOrder,
  setFindAllParticularitiesPage,
  setFindAllParticularitiesSort,
  setFindAllParticularitiesStatus,
  setShowNewParticularityModal,
  setCreateParticularityStatus,
  setEditParticularityNameStatus,
  setSelectedParticularity,
  setDeleteParticularityStatus,
  setCreateParticularityResponse,
  setFindAllParticularitiesItemsPerPage,
  setUpdateParticularityStatus,
  setParticularityToUpdate
} = particularitiesSlice.actions;

export default particularitiesSlice.reducer;
